import { FC, useEffect } from 'react';
import Button from '../../Button';
import { IStaff } from '../../../models/employees.model';
import moment from 'moment';
import { Descriptions } from 'antd';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../hooks/useAuth';
import { FcUnlock } from 'react-icons/fc';
import useAxios from '../../../hooks/useAxios';
import { unlockAccount } from '../../../api/employees.api';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import useNotification from '../../../hooks/useNotification';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { addHyphen } from '../../../lib/util';

const Block = styled.div`
  .desc-wrapper {
    width: calc(100% - 120px);
  }

  .icon-login {
    width: 20px;
    cursor: pointer;
  }

  .unlock-icon {
    margin-right: 5px;
    * {
      font-size: 20px;
    }
  }
`;

interface IProps {
  staff: IStaff | undefined;
  onSwitchAuth: () => void;
  renderCampusName: (campusCode: string[]) => string | undefined;
  onEditStaff: () => void;
  setAddCampusVisible: (addCampusVisible: boolean) => void;
  campusDeactived: boolean;
}

const StaffSummary: FC<IProps> = ({
  staff,
  onSwitchAuth,
  renderCampusName,
  onEditStaff,
  setAddCampusVisible,
  campusDeactived,
}) => {  
  const { t, i18n } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { auth } = useSelector((state: RootState) => state.auth);
  const history = useHistory();
  const { successNotification } = useNotification();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('campus.staff.message8'));
    }
  }, [submitRes, submitIsLoading]);

  const onUnlockAccount = (memberCode: string) => {
    submitExecute(unlockAccount(memberCode));
  }

  const getPhoto = () => {
    if (!staff) return null;

    if (staff.img?.thumbnailLocation) {
      return staff.img.thumbnailLocation;
    } else {
      return staff.img?.fileLocation;
    }
  };

  const isLoginPermitted = () => {
    if (!staff || auth?.hqYn !== 'Y') return false;
    if (staff.hqYn === 'Y' && auth?.groupNo !== 1000) return false;
    return true;
  }

  return (
    <Block>
      {submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={submitError} />

      <div className='comm-table__area rowType mt-0'>
        <div className='profile'>
          <div className='profile-image'>
            {getPhoto() ? (
              <img
                className="img-preview"
                src={getPhoto() || undefined}
                alt="staff"
                style={{
                  objectFit: 'contain',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              />
            ) : null}
          </div>
        </div>
        <table>
          <tbody>
            <tr>
              <th>{t('campus.staff.column35')}</th>
              <td>{staff?.memberName? (i18n.language === 'ko' ? staff?.memberName : staff?.memberEngName) : '-'}</td>
              <th>{t('campus.staff.column36')}</th>
              <td colSpan={3}>
                <div className="flex flex-justify-between">
                  {staff?.webId || '-'}
                  <div className="flex flex-juistfy-end">
                    {
                      staff && writeAuthYn
                        ? (
                          <span className="unlock-icon">
                            <FcUnlock
                              className="pointer"
                              onClick={() => onUnlockAccount(staff.memberCode)}
                            />
                          </span>
                          )
                        : null
                    }
                    {
                      isLoginPermitted()
                        ? (
                          <img
                            className="icon-login"
                            src="/images/icon-login.png"
                            alt={t('auth.login.title')}
                            onClick={onSwitchAuth}
                          />
                          )
                        : null
                    }
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <th>{t('campus.staff.column37')}</th>
              <td>{staff?.birthDay ? moment(staff.birthDay).format('YYYY.MM.DD') : '-'}</td>
              <th>{t('campus.staff.column34')}</th>
              <td>{staff?.telephoneNo || '-'}</td>
              <th>{t('campus.staff.column32')}</th>
              <td style={{width:200, wordBreak:"break-all"}}>{staff?.campusCode ? renderCampusName(staff.campusCode) : '-'}</td>
            </tr>
            <tr>
              <th>{t('campus.staff.column33')}</th>
              <td>{staff?.memberEngName || '-'}</td>
              <th>{t('common.column.group')}</th>
              <td>{staff?.groupName ? (i18n.language === 'ko' ? staff?.groupName : staff?.groupEngName) : '-'}</td>
              <th>{t('common.text.phoneNum')}</th>
              <td>{staff?.mobilePhone ? addHyphen(staff.mobilePhone) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          {
            auth && auth.hqYn === 'Y' && staff?.hqYn === 'N'
              ? (
                <Button
                  className='btn-basic pink-line'
                  onClick={() => {
                    if (!staff) return;
                    setAddCampusVisible(true);
                  }}
                  text={t('campus.staff.button6')}
                  disabled={campusDeactived}
                />
              )
              : null
          }
          <Button
            className='btn-basic pink-line'
            onClick={() => {
              if (!staff) return;
              sessionStorage.setItem('memberCode', staff?.memberCode || '');
              history.push('/message/write');
            }}
            text={t('campus.staff.button7')}
          />
          <Button
            className='btn-basic pink-line'
            onClick={onEditStaff}
            text={t('campus.staff.button8')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
    </Block>
  );
}

export default StaffSummary;
